import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { StarRating } from './StarRating';
import { ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATE_MS = 8000;

// Session-lived cache so Home + Shop share one request.
let reelCache = null;
let reelInflight = null;
function fetchRecentReviews() {
  if (reelCache) return Promise.resolve(reelCache);
  if (!reelInflight) {
    reelInflight = axios
      .get(`${API}/reviews/recent?limit=15`)
      .then((r) => { reelCache = Array.isArray(r.data) ? r.data : []; return reelCache; })
      .catch(() => []);
  }
  return reelInflight;
}

// A single compact review card. Measures its own text to decide whether the
// clamped comment overflows 3 lines → shows a "Read more" link to the product.
const ReelCard = ({ review }) => {
  const textRef = useRef(null);
  const [overflow, setOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) setOverflow(el.scrollHeight - el.clientHeight > 2);
  }, [review]);

  const more = overflow && review.product_slug;

  return (
    <div className="h-full min-h-[172px] rounded-2xl border border-shrimp-border/50 bg-white p-5 flex flex-col gap-2.5 shadow-[0_8px_20px_-12px_rgba(20,40,30,0.30)] text-left transition-shadow duration-200 hover:shadow-[0_10px_22px_-12px_rgba(20,40,30,0.42)]" data-testid={`reel-card-${review.id}`}>
      <div className="flex items-center justify-between gap-2">
        <StarRating value={review.rating} size="sm" />
        {review.product_slug ? (
          <Link to={`/product/${review.product_slug}`} className="text-[11px] font-medium text-shrimp-green/90 hover:text-shrimp-green hover:underline truncate max-w-[55%] text-right" title={review.product_name}>
            {review.product_name}
          </Link>
        ) : (
          <span className="text-[11px] text-shrimp-text/70 truncate max-w-[55%] text-right">{review.product_name}</span>
        )}
      </div>
      <p ref={textRef} className="text-sm text-shrimp-text/90 leading-relaxed line-clamp-3 flex-1">{review.comment}</p>
      <div className="flex items-center justify-between gap-2 mt-auto pt-1 border-t border-shrimp-border/40">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-shrimp-dark pt-1.5">
          {review.display_name}
          {review.verified_purchase && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" title="Verified purchase" />}
        </span>
        {more && (
          <Link to={`/product/${review.product_slug}`} className="text-xs font-semibold text-shrimp-green hover:underline shrink-0 pt-1.5" data-testid={`reel-read-more-${review.id}`}>
            Read more →
          </Link>
        )}
      </div>
    </div>
  );
};

// Non-intrusive auto-rotating reel of recent real customer reviews.
// Shows `perView` cards (1 / 2 / 3 by breakpoint) and advances a whole page
// (the next `perView`) every 8s as a slideshow.
export const ReviewsReel = ({ onDark = false, className = '' }) => {
  const [reviews, setReviews] = useState(reelCache || []);
  const [perView, setPerView] = useState(3);
  const [page, setPage] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [sliding, setSliding] = useState(false);
  const paused = useRef(false);

  useEffect(() => {
    let mounted = true;
    fetchRecentReviews().then((data) => { if (mounted) setReviews(data); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w < 640 ? 1 : w < 1024 ? 2 : 3);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const pageCount = perView > 0 ? Math.ceil(reviews.length / perView) : 1;
  const canRotate = pageCount > 1;

  // Reset to the first page whenever the layout or data changes.
  useEffect(() => { setPage(0); setAnimate(true); }, [perView, reviews.length]);

  useEffect(() => {
    if (!canRotate) return;
    const t = setInterval(() => {
      if (!paused.current) { setSliding(true); setPage((p) => p + 1); }
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [canRotate, pageCount]);

  // Seamless loop: after sliding onto the cloned first page, snap back silently.
  const handleTransitionEnd = () => {
    setSliding(false);
    if (page >= pageCount) { setAnimate(false); setPage(0); }
  };
  useEffect(() => {
    if (!animate) {
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      return () => cancelAnimationFrame(id);
    }
  }, [animate]);

  if (!reviews.length) return null;

  const cardPct = 100 / perView;
  let items;
  if (canRotate) {
    items = [];
    for (let p = 0; p < pageCount; p++) {
      for (let k = 0; k < perView; k++) {
        items.push({ r: reviews[(p * perView + k) % reviews.length], key: `p${p}-${k}` });
      }
    }
    // Clone the first page at the end for a seamless wrap.
    for (let k = 0; k < perView; k++) items.push({ r: reviews[k % reviews.length], key: `clone-${k}` });
  } else {
    items = reviews.map((r, i) => ({ r, key: `s-${i}` }));
  }
  const translate = canRotate ? page * 100 : 0;

  return (
    <div className={`w-full max-w-5xl mx-auto ${className}`} data-testid="reviews-reel">
      <div className="flex items-center justify-center gap-3 mb-3">
        <span className={`h-px w-8 ${onDark ? 'bg-white/40' : 'bg-shrimp-border'}`} />
        <p className={`text-center text-xs font-semibold uppercase tracking-[0.18em] ${onDark ? 'text-white/85' : 'text-shrimp-text/70'}`}>
          What our customers say
        </p>
        <span className={`h-px w-8 ${onDark ? 'bg-white/40' : 'bg-shrimp-border'}`} />
      </div>
      <div
        className="overflow-hidden py-4 -my-2"
        style={sliding ? {
          WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
          maskImage: 'linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
        } : undefined}
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => { paused.current = false; }}
      >
        <div
          className={`flex ${!canRotate ? 'justify-center' : ''}`}
          style={{
            transform: `translateX(-${translate}%)`,
            transition: animate ? 'transform 600ms ease-in-out' : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {items.map(({ r, key }) => (
            <div key={key} className="shrink-0 min-w-0 px-3" style={{ flex: `0 0 ${cardPct}%` }}>
              <ReelCard review={r} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewsReel;
